// src/pages/ChatbotSettings/Prompt.js
import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import CreatePrompts from "../../../components/CreatePrompts";
import {
  Divider,
  Drawer,
  Box,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Paper,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#3b82f6',
};

const DEFAULT_PRIMARY_COLOR = "#353535";
const DEFAULT_SECONDARY_COLOR = "#ffffff";

const colorPickerWrapperStyle = (theme) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
});

const colorPreviewStyle = (color) => ({
  width: "24px",
  height: "24px",
  backgroundColor: color,
  borderRadius: "50%",
  cursor: "pointer",
  border: "1px solid #ccc",
  marginRight: "10px"
});

const colorInputHiddenStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  opacity: 0,
  cursor: "pointer"
};

const colorHexStyle = { fontFamily: "monospace" };

const Prompt = forwardRef(({
  chatbotId: propChatbotId,
  isSetupMode = false,
  initialChatbotNameFromSetup = "",
  onComplete,
  onError
}, ref) => {
  const theme = useTheme();
  const [message, setMessage] = useState("");
  const [chatbotName, setChatbotName] = useState(initialChatbotNameFromSetup || "");
  const [chatbotNameError, setChatbotNameError] = useState("");
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY_COLOR);
  const [prompt, setPrompt] = useState("");
  const [greeting, setGreeting] = useState("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [effectiveChatbotId, setEffectiveChatbotId] = useState(null);

  const primaryColorInputRef = useRef(null);
  const secondaryColorInputRef = useRef(null);
  const createPromptsRef = useRef(null);

  const { chatbotId: paramsChatbotId } = useParams();

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  const [launcherDesktopState, setLauncherDesktopState] = useState("closed");
  const [launcherMobileState, setLauncherMobileState] = useState("open");
  const [launcherPosition, setLauncherPosition] = useState("right");

  const [chatbotLogo, setChatbotLogo] = useState("");
  const [chatbotLogoText, setChatbotLogoText] = useState("Ask Me");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const logoInputRef = useRef(null);

  // Clear success/error message when any field is edited
  const clearMessage = () => {
    if (message) { setMessage(""); setIsSaved(false); }
  };

  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission('chatbot-settings', 'view');
        const modifyPerm = await hasPermission('chatbot-settings', 'add|edit');
        setCanView(viewPerm);
        setCanModify(modifyPerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
        setCanModify(false);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    if (propChatbotId) {
      setEffectiveChatbotId(propChatbotId);
    } else {
      setEffectiveChatbotId(paramsChatbotId);
    }
  }, [propChatbotId, paramsChatbotId]);

  const fetchLLMSettings = async (isInitialFetch = false, currentChatbotId) => {
    if (!currentChatbotId) return;

    if (!isSetupMode || isInitialFetch) {
      try {
        const params = { chatbot_id: currentChatbotId };
        const response = await axiosInstance.get(`/get-llm-settings`, { params });
        if (response.data && response.data.success && typeof response.data.data !== 'undefined') {
          const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

          if (!isSetupMode) {
            setChatbotName(responseData.chatbot_name || "");
          }
          setPrimaryColor(responseData.primary_color || DEFAULT_PRIMARY_COLOR);
          setSecondaryColor(responseData.secondary_color || DEFAULT_SECONDARY_COLOR);
          setPrompt(responseData.prompt_text || "");
          setGreeting(responseData.greeting_text || "");

          setLauncherDesktopState(responseData.launcher_desktop_default_state || "closed");
          setLauncherMobileState(responseData.launcher_mobile_default_state || "open");
          setLauncherPosition(responseData.launcher_position || "right");
          setChatbotLogo(responseData.chatbot_logo || "");
          setChatbotLogoText(responseData.chatbot_logo_text || "Ask Me");
          setLogoFile(null);
          setLogoPreview(null);
          setRemoveLogo(false);

        } else {
          if (!isSetupMode) {
            const errorMsg = (response.data && !response.data.success)
              ? response.data.message
              : "Could not load settings due to unexpected data format.";
            setMessage(errorMsg || "Could not load settings.");
          }
          setPrompt("");
          setGreeting("");
          setPrimaryColor(DEFAULT_PRIMARY_COLOR);
          setSecondaryColor(DEFAULT_SECONDARY_COLOR);
        }
      } catch (error) {
        if (!isSetupMode) {
          setMessage("Error fetching Chatbot settings.");
        }
        setPrimaryColor(DEFAULT_PRIMARY_COLOR);
        setSecondaryColor(DEFAULT_SECONDARY_COLOR);
        setPrompt("");
        setGreeting("");
      }
    } else if (isSetupMode && initialChatbotNameFromSetup) {
      setChatbotName(initialChatbotNameFromSetup);
    }
  };

  useEffect(() => {
    fetchLLMSettings(true, effectiveChatbotId);
    if (isSetupMode && initialChatbotNameFromSetup) {
      setChatbotName(initialChatbotNameFromSetup);
    }
  }, [effectiveChatbotId, isSetupMode, initialChatbotNameFromSetup]);

  const refreshLLMSettingsData = async (successMessage = "New prompt applied successfully!") => {
    await fetchLLMSettings(false, effectiveChatbotId);
    if (isSetupMode && initialChatbotNameFromSetup) {
      setChatbotName(initialChatbotNameFromSetup);
    }
    setMessage(successMessage);
    setIsDrawerOpen(false);
    setTimeout(() => setMessage(""), 3000);
  };

  const triggerSave = async () => {
    setIsSaving(true);
    setIsSaved(false);
    setMessage("");
    setChatbotNameError("");

    if (!effectiveChatbotId) {
      const idErrorMsg = "Chatbot ID is not available. Cannot save settings.";
      if (isSetupMode && onError) onError(idErrorMsg);
      else setMessage(idErrorMsg);
      setIsSaving(false);
      return false;
    }

    const currentName = (isSetupMode && initialChatbotNameFromSetup) ? initialChatbotNameFromSetup : chatbotName;

    if (!isSetupMode) {
      if (currentName.trim().length > 30) {
        setChatbotNameError("Chatbot name must not be more than 30 characters long.");
        setIsSaving(false);
        if (isSetupMode && onError) onError("Chatbot name validation issue.");
        return false;
      }
      if (!currentName.trim()) {
        setChatbotNameError("Chatbot name is required.");
        setIsSaving(false);
        if (isSetupMode && onError) onError("Chatbot name is required.");
        return false;
      }
    }

    if (!prompt.trim()) {
      const promptErrorMsg = "Prompt text cannot be empty.";
      setMessage(promptErrorMsg);
      setIsSaving(false);
      if (isSetupMode && onError) onError(promptErrorMsg);
      return false;
    }
    if (!greeting.trim()) {
      const greetingErrorMsg = "Greeting text cannot be empty.";
      setMessage(greetingErrorMsg);
      setIsSaving(false);
      if (isSetupMode && onError) onError(greetingErrorMsg);
      return false;
    }

    // Use FormData when logo file is present, otherwise JSON
    let requestConfig = {};
    let payload;

    if (logoFile || removeLogo) {
      const formData = new FormData();
      formData.append('chatbot_name', currentName.trim());
      formData.append('prompt_text', prompt.trim());
      formData.append('greeting_text', greeting.trim());
      formData.append('primary_color', primaryColor);
      formData.append('secondary_color', secondaryColor);
      formData.append('launcher_desktop_default_state', launcherDesktopState);
      formData.append('launcher_mobile_default_state', launcherMobileState);
      formData.append('launcher_position', launcherPosition);
      formData.append('chatbot_logo_text', chatbotLogoText.trim() || 'Ask Me');
      formData.append('is_chatbot_setting', isSetupMode);
      formData.append('chatbot_id', effectiveChatbotId);
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      if (removeLogo) {
        formData.append('remove_logo', 'true');
      }
      payload = formData;
      requestConfig = { headers: { 'Content-Type': 'multipart/form-data' } };
    } else {
      payload = {
        chatbot_name: currentName.trim(),
        prompt_text: prompt.trim(),
        greeting_text: greeting.trim(),
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        launcher_desktop_default_state: launcherDesktopState,
        launcher_mobile_default_state: launcherMobileState,
        launcher_position: launcherPosition,
        chatbot_logo_text: chatbotLogoText.trim() || 'Ask Me',
        is_chatbot_setting: isSetupMode,
        chatbot_id: effectiveChatbotId
      };
    }

    try {
      const response = await axiosInstance.post(`/add-llm-settings`, payload, requestConfig);

      if (response.data && response.data.success) {
        try {
          let userData = {};
          const userStrLc = localStorage.getItem('user');
          if (userStrLc) userData = JSON.parse(userStrLc);
          if (!userData.llm_settings) userData.llm_settings = {};
          userData.llm_settings.chatbot_name = currentName.trim();
          userData.llm_settings.prompt_text = prompt.trim();
          userData.llm_settings.greeting_text = greeting.trim();
          if (!isSetupMode) {
            userData.llm_settings.primary_color = primaryColor;
            userData.llm_settings.secondary_color = secondaryColor;
          }
          if (!userData.chatbot_setup_configuration) userData.chatbot_setup_configuration = {};
          userData.chatbot_setup_configuration.is_prompt_set = true
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (e) {
          console.error("Prompt.js: Failed to update localStorage with LLM settings:", e);
        }

        if (isSetupMode && onComplete) {
          onComplete({ nextSlug: 'chatbot-training-info' });
        } else {
          setIsSaved(true);
          setMessage(response.data.message || "Your chatbot settings have been saved.");
        }
        return true;
      } else {
        const errorMsg = response.data?.message || "Failed to save Chatbot settings.";
        if (isSetupMode && onError) onError(errorMsg);
        else setMessage(errorMsg);
        setIsSaved(false);
        return false;
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || "An error occurred while saving Chatbot settings.";
      if (isSetupMode && onError) onError(errorMsg);
      else setMessage(errorMsg);
      setIsSaved(false);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    triggerSave: triggerSave
  }));

  const toggleDrawer = (open) => () => {
    setIsDrawerOpen(open);
  };

  const createPromptSubmit = async () => {
    if (createPromptsRef.current && createPromptsRef.current.handleGeneratePrompt) {
      const generatedPrompt = await createPromptsRef.current.handleGeneratePrompt();
      if (typeof generatedPrompt === 'string' && generatedPrompt) {
        setPrompt(generatedPrompt);
        setMessage("New prompt generated. Review and save settings.");
        setIsDrawerOpen(false);
      } else if (generatedPrompt === true) {
        await refreshLLMSettingsData("New prompt generated and applied. Please save settings to confirm all changes.");
      }
    }
  };

  if (checkingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 5 }}>
        <CircularProgress />
        <Typography ml={2}>Checking permissions...</Typography>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 6 }}>
        <Typography variant="h6">Access Denied</Typography>
        You don't have permission to view Prompt settings. Please contact your administrator.
      </Alert>
    );
  }

  return (
      <Box sx={{ fontFamily, maxWidth: '1200px', width: '100%', mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
            boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
          }}>
            <SettingsIcon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            <Typography sx={{
              fontFamily,
              fontSize: '1.5rem',
              fontWeight: 700,
              color: colors.textPrimary,
              letterSpacing: '-0.02em',
            }}>
              Chatbot Settings
            </Typography>
          </Box>
          <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textTertiary, mt: 1 }}>
            {isSetupMode
              ? 'Configure the chatbot with colors and define the core prompt and greeting message'
              : 'Configure your chatbot appearance and behavior'
            }
          </Typography>
          {isSetupMode && initialChatbotNameFromSetup && (
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              bgcolor: 'rgba(59,130,246,0.1)',
              px: 2,
              py: 0.5,
              borderRadius: '20px',
              mt: 1,
            }}>
              <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: colors.accent }}>
                {initialChatbotNameFromSetup}
              </Typography>
            </Box>
          )}
        </Box>

        <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.08)', boxShadow: 'none' }}>
          <Box sx={{ p: 0 }}>
              {!isSetupMode && (
                <Box mb={4}>
                  <Typography variant="h6" mb={1} color="textPrimary">
                    Chatbot Name <span style={{ color: theme.palette.error.main }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    value={chatbotName}
                    onChange={(e) => {
                      setChatbotName(e.target.value);
                      if (chatbotNameError) setChatbotNameError('');
                      clearMessage();
                    }}
                    placeholder="Enter chatbot name (max. 30 characters)"
                    disabled={isSetupMode && !!initialChatbotNameFromSetup}
                    error={!!chatbotNameError}
                    helperText={chatbotNameError}
                    size="small"
                  />
                </Box>
              )}

              <Box display="flex" gap={2} mb={4}>
                <Box flex={1}>
                  <Typography variant="h6" mb={1} color="textPrimary">Primary Color:</Typography>
                  <Box sx={colorPickerWrapperStyle(theme)}>
                    <div style={colorPreviewStyle(primaryColor)} onClick={() => primaryColorInputRef.current?.click()} />
                    <input ref={primaryColorInputRef} type="color" value={primaryColor} onChange={(e) => { setPrimaryColor(e.target.value); clearMessage(); }} style={colorInputHiddenStyle} />
                    <div style={colorHexStyle}>{primaryColor.toUpperCase()}</div>
                  </Box>
                </Box>
                <Box flex={1}>
                  <Typography variant="h6" mb={1} color="textPrimary">Secondary Color:</Typography>
                  <Box sx={colorPickerWrapperStyle(theme)}>
                    <div style={colorPreviewStyle(secondaryColor)} onClick={() => secondaryColorInputRef.current?.click()} />
                    <input ref={secondaryColorInputRef} type="color" value={secondaryColor} onChange={(e) => { setSecondaryColor(e.target.value); clearMessage(); }} style={colorInputHiddenStyle} />
                    <div style={colorHexStyle}>{secondaryColor.toUpperCase()}</div>
                  </Box>
                </Box>
              </Box>

              {/* Chatbot Logo Upload */}
              <Box mb={4}>
                <Typography variant="h6" mb={1} color="textPrimary">Chatbot Logo:</Typography>
                <Typography variant="caption" color="textSecondary" display="block" mb={1.5}>
                  Upload a logo to replace the default launcher icon and display in the chat header. Recommended: square image, max 2MB.
                </Typography>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/svg+xml,image/webp,image/x-icon"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 2 * 1024 * 1024) {
                        setMessage("Logo file must be less than 2MB.");
                        return;
                      }
                      setLogoFile(file);
                      setLogoPreview(URL.createObjectURL(file));
                      setRemoveLogo(false);
                      clearMessage();
                    }
                  }}
                />
                <Box display="flex" alignItems="center" gap={2}>
                  {(logoPreview || (chatbotLogo && !removeLogo)) && (
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '12px',
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.background.default,
                      }}
                    >
                      <img
                        src={logoPreview || chatbotLogo}
                        alt="Chatbot logo"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                  <Box display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => logoInputRef.current?.click()}
                    >
                      {chatbotLogo && !removeLogo ? 'Change Logo' : 'Upload Logo'}
                    </Button>
                    {(chatbotLogo || logoFile) && !removeLogo && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => {
                          setRemoveLogo(true);
                          setLogoFile(null);
                          setLogoPreview(null);
                          if (logoInputRef.current) logoInputRef.current.value = '';
                          clearMessage();
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Chatbot Logo Text */}
              <Box mb={4}>
                <Typography variant="h6" mb={1} color="textPrimary">Chatbot Logo Text:</Typography>
                <Typography variant="caption" color="textSecondary" display="block" mb={1.5}>
                  Text displayed on the launcher icon/logo. Default is "Ask Me". Max 20 characters.
                </Typography>
                <TextField
                  fullWidth
                  value={chatbotLogoText}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.length <= 20) {
                      setChatbotLogoText(val);
                      clearMessage();
                    }
                  }}
                  placeholder="Ask Me"
                  size="small"
                  inputProps={{ maxLength: 20 }}
                  helperText={`${chatbotLogoText.length}/20 characters`}
                />
              </Box>

              <Divider sx={{ my: 4, borderColor: "divider" }} />

            <Typography variant="h6" mb={3} color="textPrimary">
                <strong>Launcher</strong> Configuration
            </Typography>

            {/* Launcher Position */}
            <Box mb={4}>
                <Typography variant="subtitle1" mb={1} color="textPrimary">
                    Launcher Position:
                </Typography>
                <Box display="flex" gap={2}>
                    <Button
                        variant={launcherPosition === 'left' ? 'contained' : 'outlined'}
                        onClick={() => { setLauncherPosition('left'); clearMessage(); }}
                        sx={{ flex: 1 }}
                    >
                        Left
                    </Button>
                    <Button
                        variant={launcherPosition === 'right' ? 'contained' : 'outlined'}
                        onClick={() => { setLauncherPosition('right'); clearMessage(); }}
                        sx={{ flex: 1 }}
                    >
                        Right
                    </Button>
                </Box>
            </Box>

            {/* Desktop Default State */}
            <Box mb={4}>
                <Typography variant="subtitle1" mb={1} color="textPrimary">
                    Desktop Launcher Default State:
                </Typography>
                <Box display="flex" gap={2}>
                    <Button
                        variant={launcherDesktopState === 'open' ? 'contained' : 'outlined'}
                        onClick={() => { setLauncherDesktopState('open'); clearMessage(); }}
                        sx={{ flex: 1 }}
                    >
                        Open
                    </Button>
                    <Button
                        variant={launcherDesktopState === 'closed' ? 'contained' : 'outlined'}
                        onClick={() => { setLauncherDesktopState('closed'); clearMessage(); }}
                        sx={{ flex: 1 }}
                    >
                        Closed
                    </Button>
                </Box>
                <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                    Default state for large screens (desktop/tablet landscape)
                </Typography>
            </Box>

            {/* Mobile Default State */}
            <Box mb={4}>
                <Typography variant="subtitle1" mb={1} color="textPrimary">
                    Mobile Launcher Default State:
                </Typography>
                <Box display="flex" gap={2}>
                    <Button
                        variant={launcherMobileState === 'open' ? 'contained' : 'outlined'}
                        onClick={() => { setLauncherMobileState('open'); clearMessage(); }}
                        sx={{ flex: 1 }}
                    >
                        Open
                    </Button>
                    <Button
                        variant={launcherMobileState === 'closed' ? 'contained' : 'outlined'}
                        onClick={() => { setLauncherMobileState('closed'); clearMessage(); }}
                        sx={{ flex: 1 }}
                    >
                        Closed
                    </Button>
                </Box>
                <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                    Default state for mobile devices (portrait)
                </Typography>
            </Box>

            <Divider sx={{ my: 4, borderColor: "divider" }} />

              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6" color="textPrimary">System Prompt:</Typography>
                <Box display="flex" alignItems="center">
                  <Button
                    onClick={toggleDrawer(true)}
                    sx={{ textTransform: 'none', textDecoration: 'underline' }}
                  >
                    Generate Prompt
                  </Button>
                  <Tooltip title="The system prompt guides the AI's personality, role, and response style.">
                    <IconButton size="small">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={isSetupMode ? 6 : 8}
                value={prompt}
                onChange={(e) => { setPrompt(e.target.value); clearMessage(); }}
                placeholder="e.g., You are a helpful assistant for KlonIT. Answer questions about our services and provide support."
                sx={{ mb: 3 }}
              />

              <Typography variant="h6" mb={1} color="textPrimary">
                Greeting Message: <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={isSetupMode ? 4 : 8}
                value={greeting}
                onChange={(e) => { setGreeting(e.target.value); clearMessage(); }}
                placeholder="e.g., Hello! I'm the KlonIT assistant. How can I help you today?"
                sx={{ mb: 3 }}
              />

              {!isSetupMode && canModify && (
                <Button
                  variant="contained"
                  onClick={triggerSave}
                  disabled={isSaving}
                  startIcon={isSaving ? <CircularProgress size={16} /> : isSaved ? <CheckIcon /> : <SaveIcon />}
                  fullWidth={false}
                >
                  {isSaving ? "Saving..." : isSaved ? "Saved!" : "Save Settings"}
                </Button>
              )}

              {!isSetupMode && message && (
                <Alert
                  severity={
                    isSaved && message.toLowerCase().includes("success")
                      ? 'success'
                      : (message.toLowerCase().includes("error") ||
                        message.toLowerCase().includes("failed") ||
                        message.toLowerCase().includes("cannot be empty") ||
                        message.toLowerCase().includes("required"))
                        ? 'error'
                        : 'info'
                  }
                  sx={{ mt: 3 }}
                >
                  {message}
                </Alert>
              )}

          <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={toggleDrawer(false)}
            PaperProps={{
              sx: {
                width: { xs: '100%', sm: 500 },
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#475569' : '#cbd5e1',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? '#64748b' : '#94a3b8',
                  },
                },
              }
            }}
          >
            <Box sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" color="textPrimary">Generate New Prompt</Typography>
                <IconButton onClick={toggleDrawer(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <CreatePrompts
                ref={createPromptsRef}
                isSetupMode={isSetupMode}
                chatbotId={effectiveChatbotId}
                onFormValidityChange={() => { }}
                onComplete={(data) => {
                  setPrompt(data.prompt_text);
                  setGreeting(data.greeting_text);
                  setIsDrawerOpen(false);
                }}
                onError={(errorMessage) => {
                  setMessage(errorMessage || "Error during prompt generation.");
                  setIsDrawerOpen(false);
                }}
              />
              {canModify && (
                <Box mt={3}>
                  <Button variant="contained" fullWidth onClick={createPromptSubmit}>
                    Generate & Apply to Form
                  </Button>
                </Box>
              )}
            </Box>
          </Drawer>
        </Box>
        </Paper>
      </Box>
  );
});

export default Prompt;